import PropTypes from 'prop-types';
import React from 'react';
import dynamic from 'next/dynamic';

import StyledFullscreenMedia from './fullscreenMedia.style';
import Headline from '../../atoms/headline/headline';
import MixedCopy from '../mixed-copy/mixedCopy';
import ImageSkeleton from '../../atoms/image/image.skeleton';
import VideoSkeleton from '../../atoms/video/video.skeleton';
import PanoImageSkeleton from '../../atoms/pano-image/panoImage.skeleton';
import Copy from '../../atoms/copy/copy';
import Container from '../../layout/container/container';

const PanoImage = dynamic({
  loader: () => import(/* webpackChunkName: "pano-image" */ '../../atoms/pano-image/panoImage'),
  loading: () => <PanoImageSkeleton />,
  ssr: false
});

const Image = dynamic({
  loader: () => import(/* webpackChunkName: "image" */ '../../atoms/image/image'),
  loading: () => <ImageSkeleton />,
  ssr: false
});

const Video = dynamic({
  loader: () => import(/* webpackChunkName: "video" */ '../../atoms/video/video'),
  loading: () => <VideoSkeleton />,
  ssr: false
});

const videoIntroOrImage = (videoPath = null, hoster = null, videoId = null, previewImage) => {
  if (videoPath) {
    return <Video videoPath={videoPath} previewImage={previewImage} />;
  }
  if (videoId && hoster) {
    return <Video hoster={hoster} videoId={videoId} previewImage={previewImage} />;
  }
  return <Image {...previewImage} lazy="default" />;
};

const sizes = [{ width: '90vw' }];

const FullscreenMedia = ({
  type,
  file,
  imageLink,
  isExternalLink = false,
  videoId,
  previewImage,
  caption,
  headline,
  topline,
  copy,
  credits,
  alignment = 'left',
  videoPath,
  vtt,
  hoster,
  hasBackground = true,
  customTheme,
  insideContainer = false
}) => {
  const MediaWrapperTag = type === 'fullwidthImage' && imageLink ? 'a' : 'div';

  return (
    <StyledFullscreenMedia
      alignment={alignment}
      hasHeadline={headline}
      hasBackground={hasBackground}
      customTheme={customTheme}
      insideContainer={insideContainer}
      type={type}
    >
      {headline && (
        <div className="media__content-section with--spacing">
          <div className="media__content">
            <Container className="media__content-container">
              <div className="media__content-intro">
                {headline && (
                  <div className="media__content-headlines">
                    {topline && (
                      <Headline tag="h3" className="stage__subline" subline>
                        {topline}
                      </Headline>
                    )}
                    {headline && (
                      <Headline tag="h2" primary>
                        {headline}
                      </Headline>
                    )}
                  </div>
                )}
              </div>
              {copy && <MixedCopy fullWidth html={copy} className="media__content-copy" />}
            </Container>
          </div>
        </div>
      )}
      <Container className="media__media-section" large>
        <figure>
          <MediaWrapperTag
            href={imageLink && imageLink}
            title={imageLink && file.caption}
            target={isExternalLink && '_blank'}
            rel={isExternalLink && 'noopener noreferrer'}
            className="media__wrapper"
          >
            {type === 'fullwidthImage' && <Image {...file} lazy="default" sizes={sizes} />}
            {type === 'embeddedVideo' && (
              <Video hoster={hoster} videoId={videoId} previewImage={previewImage} />
            )}
            {type === 'video' && (
              <Video videoPath={videoPath} vtt={vtt} previewImage={previewImage} />
            )}
            {type === 'introVideo' && videoIntroOrImage(videoPath, hoster, videoId, previewImage)}
            {type === 'vrImage' && <PanoImage file={file} previewImage={previewImage} />}
          </MediaWrapperTag>
        </figure>
      </Container>
      {!headline && (
        <>
          {(caption || (file && file.caption)) && (
            <Copy
              tag="figcaption"
              className="media__caption"
              small
              dangerouslySetInnerHTML={{ __html: caption || (file && file.caption) }}
            />
          )}
          {(credits || (file && file.credits)) && (
            <Copy
              tag="figcaption"
              className="media__credits"
              small
              dangerouslySetInnerHTML={{ __html: credits || (file && file.credits) }}
            />
          )}
        </>
      )}
      {headline && (
        <div className="media__content-section">
          <div className="media__content">
            <Container className="media__content-container">
              <div className="media__content-intro">
                <div className="media__content-hints">
                  {(caption || (file && file.caption)) && (
                    <Copy
                      tag="figcaption"
                      className="media__content-caption"
                      small
                      dangerouslySetInnerHTML={{ __html: caption || (file && file.caption) }}
                    />
                  )}
                  {(credits || (file && file.credits)) && (
                    <Copy
                      tag="figcaption"
                      className="media__content-credits"
                      small
                      dangerouslySetInnerHTML={{ __html: credits || (file && file.credits) }}
                    />
                  )}
                </div>
              </div>
            </Container>
          </div>
        </div>
      )}
    </StyledFullscreenMedia>
  );
};

FullscreenMedia.propTypes = {
  type: PropTypes.oneOf(['fullwidthImage', 'embeddedVideo', 'vrImage', 'video', 'introVideo']),
  caption: PropTypes.string,
  credits: PropTypes.string,
  headline: PropTypes.string,
  topline: PropTypes.string,
  copy: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'right']),
  file: PropTypes.oneOfType([PropTypes.shape(), PropTypes.string]),
  imageLink: PropTypes.string,
  isExternalLink: PropTypes.bool,
  videoId: PropTypes.string,
  hoster: PropTypes.string,
  videoPath: PropTypes.string,
  vtt: PropTypes.string,
  previewImage: PropTypes.shape(),
  hasBackground: PropTypes.bool,
  customTheme: PropTypes.string,
  insideContainer: PropTypes.bool
};

export default FullscreenMedia;
